import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./_utils";
import { Spinner } from "./spinner";

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-xs rounded-[var(--radius-control)] " +
    "font-[family-name:var(--font-ui)] font-semibold text-[13px] transition-colors " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus " +
    "disabled:opacity-50 disabled:pointer-events-none",
  {
    variants: {
      variant: {
        // Accent blue = human attention; the control shadow lifts it slightly.
        primary:
          "bg-accent text-text-inverse shadow-[var(--shadow-control)] hover:bg-accent-hover",
        // Print surface: quiet, on the page.
        secondary:
          "bg-[var(--print-bg)] text-text-primary border border-[var(--print-border)] hover:bg-[rgba(var(--glass-rgb),0.7)]",
        // Hover is a white bump, never a color shift.
        ghost: "bg-transparent text-[var(--text-quiet)] hover:bg-[rgba(var(--glass-rgb),0.6)]",
        destructive: "bg-error text-text-inverse hover:opacity-90",
        accent:
          "bg-transparent text-accent border border-[var(--accent-border)] hover:bg-[rgba(var(--glass-rgb),0.6)]",
        dark: "bg-[var(--brand-dark)] text-[var(--brand-white)] hover:opacity-90",
      },
      size: {
        sm: "h-9 md:h-7 px-md",
        md: "h-10 md:h-9 px-lg",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  }
);

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    loading?: boolean;
  };

/**
 * Primary action button.
 *
 * @example
 * <Button>Save</Button>
 * <Button variant="secondary" size="sm">Cancel</Button>
 * <Button variant="destructive">Delete</Button>
 * <Button loading>Saving…</Button>
 */
export function Button({
  className,
  variant,
  size,
  loading,
  disabled,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Spinner size="sm" aria-hidden="true" data-testid="spinner" />}
      {children}
    </button>
  );
}
